import { formatCurrency } from '../utils/money'
import { useState, useEffect } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import Navbar from '../components/Navbar'
import { useCartStore } from '../stores/cartStore'
import { itemsApi, settingsApi } from '../lib/api'
import { useAuth } from '../context/AuthContext'
import { calculateRentalPrice, calculateServiceFee, calculateTotalAmount } from '../utils/pricing'
import { calculateDistanceMeters } from '../utils/geo'
import { assetUrl } from '../utils/assetUrl'
import { hasPhysicalAddress } from '../utils/locations'
import ReportDialog from '../components/ReportDialog'

interface ItemDetailData {
  id: string
  title: string
  description: string
  sku: string | null
  serialNumber: string | null
  dailyPrice: number | null
  weeklyPrice: number | null
  monthlyPrice: number | null
  salePrice: number | null
  securityDeposit: number | null
  listingType: 'RENT' | 'SALE' | 'RENT_OR_SALE'
  condition: 'LIKE_NEW' | 'GOOD' | 'FAIR'
  category: string
  addressApprox: string
  allowPickup: boolean
  pickupLocationLabel: string | null
  minRentalDays: number | null
  weightLbs: number | null
  lengthIn: number | null
  widthIn: number | null
  heightIn: number | null
  photos: Array<{ id: string; url: string; isDefault?: boolean }>
  hoursRented: number
  totalRentals: number
  // False when the owner has no physical pickup address saved; the item cannot be rented then.
  ownerHasPickupLocation?: boolean
  owner: {
    id: string
    firstName: string
    lastName: string
    avatar: string | null
    verificationStatus: string
    metadata?: {
      locations?: {
        delivery?: Array<{ id: string; name: string; address: string }>
        pickup?: Array<{ name: string; address?: string; latitude?: string | number; longitude?: string | number; isDefault?: boolean }>
      }
    }
  }
}

export default function ItemDetail() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { addItem } = useCartStore()
  const { user } = useAuth()
  const [reporting, setReporting] = useState(false)
  // Default to a ready-to-go 7-day window (tomorrow through a week later) instead of
  // making the renter pick dates before they can even see pricing -- the existing
  // effect below already defaults endDate to startDate + 7 days, so seeding
  // startDate here is enough to get both for free on mount.
  const [startDate, setStartDate] = useState(() => {
    const tomorrow = new Date()
    tomorrow.setDate(tomorrow.getDate() + 1)
    return tomorrow.toISOString().split('T')[0]
  })
  const [endDate, setEndDate] = useState('')
  const [cartError, setCartError] = useState('')
  const [addingToCart, setAddingToCart] = useState(false)
  const [deliveryOption, setDeliveryOption] = useState<'pickup' | 'shipping' | 'doordash'>('pickup')
  const [activeImage, setActiveImage] = useState(0)
  const [item, setItem] = useState<ItemDetailData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [liveDistanceMiles, setLiveDistanceMiles] = useState<number | null>(null)
  const [doorDashLimits, setDoorDashLimits] = useState({ maxWeight: 50, maxDimension: 36 })
  const [shippingQuote, setShippingQuote] = useState<{ loading: boolean; available: boolean; amount?: string; carrier?: string; reason?: string } | null>(null)

  useEffect(() => {
    settingsApi.getAll()
      .then((res) => {
        const settings: Array<{ key: string; value: string }> = res.data.data || []
        const maxWeight = Number(settings.find((s) => s.key === 'DOORDASH_MAX_WEIGHT_LBS')?.value)
        const maxDimension = Number(settings.find((s) => s.key === 'DOORDASH_MAX_DIMENSION_IN')?.value)
        setDoorDashLimits({
          maxWeight: Number.isFinite(maxWeight) ? maxWeight : 50,
          maxDimension: Number.isFinite(maxDimension) ? maxDimension : 36,
        })
      })
      .catch(() => {
        // Keep the sensible defaults -- this is a nice-to-have gate refinement,
        // not something that should block the page over a settings-fetch failure.
      })
  }, [])

  useEffect(() => {
    if (id) {
      fetchItem(id)
    }
  }, [id])

  // Live pre-request quote, shown only while Shipping is selected -- this is a
  // preview using the owner's DEFAULT pickup location; the actual cost locked in
  // at approval may differ if the owner ends up choosing a different one.
  useEffect(() => {
    if (deliveryOption !== 'shipping' || !item?.id) {
      setShippingQuote(null)
      return
    }
    let cancelled = false
    setShippingQuote({ loading: true, available: false })
    itemsApi.getShippingQuote(item.id)
      .then((res) => {
        if (cancelled) return
        setShippingQuote({ loading: false, ...res.data.data })
      })
      .catch(() => {
        if (cancelled) return
        setShippingQuote({ loading: false, available: false, reason: 'Could not load a shipping estimate right now.' })
      })
    return () => { cancelled = true }
  }, [deliveryOption, item?.id])

  // Renters requesting Pickup with no saved shipping address never got any
  // distance heads-up at all -- the backend pre-approval estimate needs a
  // saved shipping/drop-off location or profile location on file, and most
  // first-time renters have neither yet at request time. A one-shot, silent,
  // best-effort live location check here (same pattern PickupTracker.tsx uses
  // once the renter is actually en route) catches that gap at the one moment
  // it's still useful: before they submit, not after. Never blocks -- denial,
  // failure, or no resolvable pickup coordinates just leaves this off entirely.
  //
  // Pickup coordinates come from the owner's saved pickup location matching
  // item.pickupLocationLabel (Item itself has no lat/lng of its own -- that
  // lives on the owner's Profile > Locations entry, same source the backend's
  // pre-approval estimate and PickupTracker's live geofence both already use).
  const ownerPickupLocations = item?.owner.metadata?.locations?.pickup || []
  const matchedPickupLocation =
    ownerPickupLocations.find((l) => l.name === item?.pickupLocationLabel)
    || ownerPickupLocations.find((l) => l.isDefault)
    || ownerPickupLocations[0]
  const pickupLat = matchedPickupLocation?.latitude != null ? Number(matchedPickupLocation.latitude) : null
  const pickupLng = matchedPickupLocation?.longitude != null ? Number(matchedPickupLocation.longitude) : null

  useEffect(() => {
    if (deliveryOption !== 'pickup') return
    if (liveDistanceMiles !== null) return
    if (!pickupLat || !pickupLng) return
    if (!navigator.geolocation) return

    let cancelled = false
    navigator.geolocation.getCurrentPosition(
      (position) => {
        if (cancelled) return
        const meters = calculateDistanceMeters(
          position.coords.latitude,
          position.coords.longitude,
          pickupLat,
          pickupLng
        )
        setLiveDistanceMiles(meters / 1609.34)
      },
      () => {
        // Denied/unavailable -- say nothing, same as PickupTracker's approach
        // of never forcing a permission prompt to be meaningful.
      },
      { enableHighAccuracy: false, maximumAge: 300000, timeout: 5000 }
    )
    return () => {
      cancelled = true
    }
  }, [deliveryOption, pickupLat, pickupLng, liveDistanceMiles])

  // When start date changes, auto-default end date to 7 days later if the
  // renter hasn't picked one yet (or the current end date is before the new start).
  useEffect(() => {
    if (!startDate) return
    const defaultEnd = new Date(startDate)
    defaultEnd.setDate(defaultEnd.getDate() + 7)
    const defaultEndStr = defaultEnd.toISOString().split('T')[0]
    if (!endDate || endDate < startDate) {
      setEndDate(defaultEndStr)
    }
  }, [startDate])

  const fetchItem = async (itemId: string) => {
    setLoading(true)
    try {
      const response = await itemsApi.getById(itemId)
      const data = response.data

      if (data.success) {
        const parsed = {
          ...data.data,
          dailyPrice: data.data.dailyPrice ? parseFloat(data.data.dailyPrice) : null,
          weeklyPrice: data.data.weeklyPrice ? parseFloat(data.data.weeklyPrice) : null,
          monthlyPrice: data.data.monthlyPrice ? parseFloat(data.data.monthlyPrice) : null,
          salePrice: data.data.salePrice ? parseFloat(data.data.salePrice) : null,
        }
        setItem(parsed)
      } else {
        setError(data.error || 'Item not found')
      }
    } catch (err) {
      setError('Failed to load item')
    } finally {
      setLoading(false)
    }
  }

  const calculateTotal = () => {
    if (!startDate || !endDate || !item?.dailyPrice) return null
    const start = new Date(startDate)
    const end = new Date(endDate)
    const days = Math.max(1, Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)))
    const dailyPrice = parseFloat(String(item.dailyPrice))
    const weeklyPrice = item.weeklyPrice ? parseFloat(String(item.weeklyPrice)) : null
    const monthlyPrice = item.monthlyPrice ? parseFloat(String(item.monthlyPrice)) : null

    const price = calculateRentalPrice(dailyPrice, weeklyPrice, monthlyPrice, days)
    const hasDiscount = price < dailyPrice * days
    return { days, price, hasDiscount }
  }

  const handleAddToCart = async () => {
    if (!item) return
    setCartError('')
    const calculation = calculateTotal()
    if (!calculation || calculation.days <= 0) {
      setCartError('Select a start and end date above to continue.')
      return
    }
    if (item.minRentalDays && calculation.days < item.minRentalDays) {
      setCartError(`This item requires a minimum rental of ${item.minRentalDays} day${item.minRentalDays !== 1 ? 's' : ''}.`)
      return
    }

    const deposit = item.securityDeposit ? parseFloat(String(item.securityDeposit)) : 0
    const itemSubtotal = calculation.price
    const serviceFee = calculateServiceFee(itemSubtotal)

    setAddingToCart(true)
    try {
      await addItem({
        id: `${item.id}-${Date.now()}`,
        itemId: item.id,
        itemName: item.title,
        itemImage: sortedPhotos[0]?.url || '',
        dailyPrice: item.dailyPrice || 0,
        weeklyPrice: item.weeklyPrice ? parseFloat(String(item.weeklyPrice)) : null,
        monthlyPrice: item.monthlyPrice ? parseFloat(String(item.monthlyPrice)) : null,
        itemSubtotal,
        deposit,
        serviceFee,
        ownerId: item.owner.id,
        ownerName: `${item.owner.firstName} ${item.owner.lastName[0]}.`,
        startDate,
        endDate,
        days: calculation.days,
        totalPrice: calculateTotalAmount(itemSubtotal, serviceFee, deposit),
        deliveryOption,
      })
      navigate('/cart')
    } catch (err: any) {
      // Navigating to /cart on failure used to make a failed add look
      // successful -- the cart page's own mount-time server sync would
      // immediately overwrite the (never-persisted) item with the real,
      // empty cart, with no error shown at all. Stay on this page and show
      // what went wrong instead, so the delivery-method choice above isn't
      // silently lost on a retry.
      setCartError(err?.response?.data?.error || 'Failed to add this item to your cart. Please try again.')
    } finally {
      setAddingToCart(false)
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col bg-[var(--bg-primary)]">
        <Navbar />
        <main className="min-h-[calc(100vh-var(--navbar-height))] flex items-center justify-center">
          <div className="text-[var(--text-secondary)]">Loading item...</div>
        </main>
      </div>
    )
  }

  if (error || !item) {
    return (
      <div className="min-h-screen flex flex-col bg-[var(--bg-primary)]">
        <Navbar />
        <main className="min-h-[calc(100vh-var(--navbar-height))] flex items-center justify-center">
          <div className="text-red-600">{error || 'Item not found'}</div>
        </main>
      </div>
    )
  }

  const total = calculateTotal()
  const isRentable = item.listingType === 'RENT' || item.listingType === 'RENT_OR_SALE'
  const isSaleable = item.listingType === 'SALE' || item.listingType === 'RENT_OR_SALE'
  const isOwnItem = item.owner.id === user?.id

  // DoorDash and Shipping both require the renter to have a shipping address.
  const renterDropOffLocations = (user?.metadata?.locations?.['drop-off'] || [])
  const hasRenterDropOff = hasPhysicalAddress(renterDropOffLocations)

  // Renting needs a physical address on both sides, whatever the delivery option.
  const rentBlockedMessage = isOwnItem
    ? null
    : item.ownerHasPickupLocation === false
      ? "This item can't be rented right now: the owner hasn't added a pickup address yet."
      : user && !hasRenterDropOff
        ? 'Add your address (street, city, state and ZIP) in Profile > Locations before renting. Owners need to know who they are renting to and where to ship.'
        : null

  // Missing weight/dimensions never blocks DoorDash -- only an item the owner
  // actually measured as too big/heavy does. Mirrors doorDashEligibility.ts server-side.
  const doorDashOversizedReason = (() => {
    if (item.weightLbs != null && item.weightLbs > doorDashLimits.maxWeight) {
      return `This item's weight (${item.weightLbs} lbs) exceeds the ${doorDashLimits.maxWeight} lb DoorDash limit.`
    }
    const dims = [item.lengthIn, item.widthIn, item.heightIn].filter((d): d is number => d != null)
    const oversized = dims.find((d) => d > doorDashLimits.maxDimension)
    if (oversized !== undefined) {
      return `This item's dimensions exceed the ${doorDashLimits.maxDimension}" DoorDash limit.`
    }
    return null
  })()
  const doorDashDisabled = !hasRenterDropOff || !!doorDashOversizedReason

  const missingLocationMessage =
    deliveryOption === 'shipping' && !hasRenterDropOff
      ? 'You need a shipping address in your profile to use this option. Add one in Profile > Locations.'
      : deliveryOption === 'doordash' && !hasRenterDropOff
        ? 'You need a shipping address in your profile to use this option. Add one in Profile > Locations.'
        : deliveryOption === 'doordash' && doorDashOversizedReason
          ? doorDashOversizedReason
          : null

  const canAddToCart = !missingLocationMessage && !rentBlockedMessage
  const pickupInfo = item.allowPickup && item.pickupLocationLabel
    ? `${item.pickupLocationLabel}`
    : item.allowPickup
      ? 'Pickup available'
      : 'Pickup not available for this listing'

  // Sort so default photo is first for initial display
  const sortedPhotos = [...item.photos].sort((a, b) => {
    if (a.isDefault === b.isDefault) return 0
    return a.isDefault ? -1 : 1
  })

  const mainPhoto = sortedPhotos[activeImage]?.url || ''

  return (
    <div className="min-h-screen flex flex-col bg-[var(--bg-primary)]">
      <Navbar />

      <main className="min-h-[calc(100vh-var(--navbar-height))]">
        <div className="container mx-auto px-4 py-6">
          <div className="grid lg:grid-cols-2 gap-8 pb-10">
            {/* Left: Images */}
            <div>
              <div className="bg-white rounded-2xl aspect-square flex items-center justify-center text-8xl mb-4 overflow-hidden">
                {mainPhoto ? (
                  <img src={assetUrl(mainPhoto)} alt={item.title} className="w-full h-full object-cover" />
                ) : (
                  <span className="text-[var(--forest-green)]">🔧</span>
                )}
              </div>
              {sortedPhotos.length > 1 && (
                <div className="flex gap-2">
                  {sortedPhotos.map((img, i) => (
                    <button
                      key={img.id}
                      onClick={() => setActiveImage(i)}
                      className={`w-16 h-16 rounded-lg flex items-center justify-center text-2xl border-2 transition-colors overflow-hidden ${
                        activeImage === i
                          ? 'border-[var(--forest-green)]'
                          : 'border-transparent bg-white hover:border-[var(--border)]'
                      }`}
                    >
                      {img.url ? (
                        <img src={assetUrl(img.url)} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <span className="text-[var(--forest-green)]">🔧</span>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Right: Details */}
            <div>
              <div className="flex items-start justify-between mb-4">
                <div>
                  <span className="bg-[var(--bg-tint)] text-[var(--forest-green)] text-xs font-bold px-3 py-1 rounded-full">
                    {item.listingType === 'RENT' ? 'RENTAL' : item.listingType === 'SALE' ? 'FOR SALE' : 'RENT OR BUY'}
                  </span>
                  <h1 className="text-2xl font-bold text-[var(--text-primary)] mt-2">{item.title}</h1>
                  <p className="text-[var(--text-muted)]">{item.category}</p>
                  {(item.sku || item.serialNumber) && (
                    <p className="text-sm text-[var(--text-muted)] mt-1">
                      {item.sku && <span className="mr-3">SKU: <span className="font-medium text-[var(--text-secondary)]">{item.sku}</span></span>}
                      {item.serialNumber && <span>Serial: <span className="font-medium text-[var(--text-secondary)]">{item.serialNumber}</span></span>}
                    </p>
                  )}
                </div>
                <button className="p-2 hover:bg-[var(--bg-tint)] rounded-full transition-colors">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                  </svg>
                </button>
              </div>

              <div className="flex items-center gap-2 mb-6">
                <span className="text-[var(--text-muted)]">({item.totalRentals} rentals)</span>
                <span className="text-[var(--text-muted)]">•</span>
                <span className="text-[var(--text-muted)]">{item.addressApprox}</span>
              </div>

              {/* Pricing */}
              <div className="bg-white rounded-xl p-6 mb-6 border border-[var(--border)]">
                {isRentable && item.dailyPrice && (
                  <div className="mb-3">
                    <div className="text-3xl font-bold text-[var(--forest-green)] mb-1">
                      ${item.dailyPrice}
                      <span className="text-lg text-[var(--text-muted)] font-normal">/day</span>
                    </div>
                    {(item.weeklyPrice || item.monthlyPrice) && (
                      <p className="text-sm text-[var(--text-muted)]">
                        {item.weeklyPrice && `${item.weeklyPrice}/week when renting 7+ days`}
                        {item.weeklyPrice && item.monthlyPrice && ' • '}
                        {item.monthlyPrice && `${item.monthlyPrice}/month when renting 30+ days`}
                      </p>
                    )}
                  </div>
                )}
                {isSaleable && item.salePrice && (
                  <div className={isRentable ? 'border-t pt-3 mt-3' : ''}>
                    <div className="text-2xl font-bold text-[var(--forest-green)]">
                      ${item.salePrice}
                      <span className="text-lg text-[var(--text-muted)] font-normal ml-2">to buy</span>
                    </div>
                  </div>
                )}
              </div>

              {/* Description */}
              <div className="bg-white rounded-xl p-6 mb-6 border border-[var(--border)]">
                <h3 className="font-semibold text-[var(--text-primary)] mb-2">Description</h3>
                <p className="text-[var(--text-muted)]">{item.description}</p>
                <div className="mt-4 pt-4 border-t border-[var(--border)]">
                  <span className="text-sm text-[var(--text-muted)]">Condition: </span>
                  <span className="font-medium text-[var(--text-primary)]">
                    {item.condition === 'LIKE_NEW' ? 'Like New' : item.condition === 'GOOD' ? 'Good' : 'Fair'}
                  </span>
                </div>
              </div>

              {isOwnItem && (
                <div className="bg-[var(--bg-tint)] rounded-xl p-6 mb-6 border border-[var(--border)]">
                  <p className="text-[var(--forest-green)] font-medium mb-4">This is your listing. You can't rent or buy your own item.</p>
                  <button
                    onClick={() => navigate(`/edit-item/${item.id}`)}
                    className="w-full bg-[var(--forest-green)] hover:bg-[var(--emerald)] text-white font-semibold py-3 rounded-lg transition-colors"
                  >
                    Edit Listing
                  </button>
                </div>
              )}

              {/* Date Selection - only for rentable items that don't belong to the viewer */}
              {!isOwnItem && isRentable && item.dailyPrice && (
                <div className="bg-white rounded-xl p-6 mb-6 border border-[var(--border)]">
                  <h3 className="font-semibold text-[var(--text-primary)] mb-4">Select Rental Dates</h3>
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">Start Date</label>
                      <input
                        type="date"
                        value={startDate}
                        onChange={(e) => { setStartDate(e.target.value); setCartError('') }}
                        min={new Date().toISOString().split('T')[0]}
                        className="w-full px-3 py-2 border border-[var(--border)] rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--forest-green)]"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">End Date</label>
                      <input
                        type="date"
                        value={endDate}
                        onChange={(e) => { setEndDate(e.target.value); setCartError('') }}
                        min={startDate || new Date().toISOString().split('T')[0]}
                        className="w-full px-3 py-2 border border-[var(--border)] rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--forest-green)]"
                      />
                    </div>
                  </div>
                  {!startDate || !endDate ? (
                    <p className="text-xs text-[var(--text-muted)] mb-4">
                      Select both dates to see pricing and add this item to your cart.
                    </p>
                  ) : item.minRentalDays && item.minRentalDays > 1 && (
                    <p className="text-xs text-[var(--text-muted)] mb-4">
                      Minimum rental: {item.minRentalDays} days
                    </p>
                  )}

                  <h3 className="font-semibold text-[var(--text-primary)] mb-2">How will you get this item?</h3>

                  {/* Pickup location label */}
                  {item.allowPickup && item.pickupLocationLabel && (
                    <div className="mb-3 p-3 rounded-lg bg-[var(--bg-tint)] border border-[var(--border)]">
                      <div className="flex items-center gap-2">
                        <i className="mdi mdi-map-marker text-[var(--forest-green)]"></i>
                        <span className="text-sm font-medium text-[var(--text-primary)]">{pickupInfo}</span>
                      </div>
                    </div>
                  )}

                  <div className="flex flex-col sm:flex-row gap-3 mb-4">
                    <label className={`flex-1 flex items-center gap-3 p-3 rounded-lg border cursor-pointer ${
                      deliveryOption === 'pickup' ? 'border-[var(--forest-green)] bg-[var(--bg-tint)]' : 'border-[var(--border)]'
                    }`}>
                      <input
                        type="radio"
                        name="deliveryOption"
                        value="pickup"
                        checked={deliveryOption === 'pickup'}
                        onChange={() => setDeliveryOption('pickup')}
                        className="accent-[var(--forest-green)]"
                      />
                      <span className="text-sm text-[var(--text-primary)]">I&apos;ll pick it up</span>
                    </label>
                    <label className={`flex-1 flex items-center gap-3 p-3 rounded-lg border ${
                      deliveryOption === 'shipping' ? 'border-[var(--forest-green)] bg-[var(--bg-tint)]' : 'border-[var(--border)]'
                    } ${!hasRenterDropOff ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                    >
                      <input
                        type="radio"
                        name="deliveryOption"
                        value="shipping"
                        checked={deliveryOption === 'shipping'}
                        disabled={!hasRenterDropOff}
                        onChange={() => setDeliveryOption('shipping')}
                        className="accent-[var(--forest-green)]"
                      />
                      <span className="text-sm text-[var(--text-primary)]">Ship it to me</span>
                    </label>
                    <label className={`flex-1 flex items-center gap-3 p-3 rounded-lg border ${
                      deliveryOption === 'doordash' ? 'border-[var(--forest-green)] bg-[var(--bg-tint)]' : 'border-[var(--border)]'
                    } ${doorDashDisabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                    >
                      <input
                        type="radio"
                        name="deliveryOption"
                        value="doordash"
                        checked={deliveryOption === 'doordash'}
                        disabled={doorDashDisabled}
                        onChange={() => setDeliveryOption('doordash')}
                        className="accent-[var(--forest-green)]"
                      />
                      <span className="text-sm text-[var(--text-primary)]">DoorDash</span>
                    </label>
                  </div>
                  {rentBlockedMessage && (
                    <div className="flex items-start gap-2 mb-4 p-3 rounded-lg bg-amber-50 border border-amber-200">
                      <i className="mdi mdi-alert text-amber-600"></i>
                      <p className="text-xs text-amber-800">
                        {rentBlockedMessage}{' '}
                        {item.ownerHasPickupLocation !== false && (
                          <Link to="/profile?tab=locations" className="font-semibold underline">Add an address</Link>
                        )}
                      </p>
                    </div>
                  )}
                  {missingLocationMessage && (
                    <div className="flex items-center gap-2 mb-4 p-3 rounded-lg bg-amber-50 border border-amber-200">
                      <i className="mdi mdi-alert text-amber-600"></i>
                      <p className="text-xs text-amber-800">{missingLocationMessage}</p>
                    </div>
                  )}

                  {deliveryOption === 'pickup' && liveDistanceMiles !== null && liveDistanceMiles > 100 && (
                    <div className="flex items-center gap-2 mb-4 p-3 rounded-lg bg-amber-50 border border-amber-200">
                      <i className="mdi mdi-map-marker-distance text-amber-600"></i>
                      <p className="text-xs text-amber-800">
                        You appear to be about {Math.round(liveDistanceMiles).toLocaleString()} mi from the pickup location — that's likely too far to drive. Consider{' '}
                        {hasRenterDropOff ? '"Ship it to me"' : 'adding a shipping address in Profile > Locations and choosing "Ship it to me"'} instead.
                      </p>
                    </div>
                  )}

                  {deliveryOption === 'shipping' && shippingQuote && (
                    <div className="mb-4 p-3 rounded-lg bg-[var(--bg-tint)] border border-[var(--border)]">
                      {shippingQuote.loading ? (
                        <p className="text-xs text-[var(--text-secondary)]">Getting a shipping estimate...</p>
                      ) : shippingQuote.available ? (
                        <>
                          <p className="text-sm text-[var(--text-primary)]">
                            Estimated shipping: <strong>${shippingQuote.amount}</strong>{shippingQuote.carrier ? ` (${shippingQuote.carrier})` : ''}
                          </p>
                          <p className="text-xs text-[var(--text-muted)] mt-1">
                            Estimated — final shipping cost is set when the owner approves and may differ slightly.
                          </p>
                        </>
                      ) : (
                        <p className="text-xs text-[var(--text-muted)]">{shippingQuote.reason || 'No shipping estimate available right now.'}</p>
                      )}
                    </div>
                  )}

                  {total && (
                    <div className="border-t border-[var(--border)] pt-4 space-y-2">
                      <div className="flex justify-between text-sm">
                        <span>{total.days} days @ {formatCurrency(item.dailyPrice)}{total.hasDiscount ? ' (discount rate applied)' : ''}</span>
                        <span>{formatCurrency(total.price)}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span>Service Fee (10%)</span>
                        <span>{formatCurrency(calculateServiceFee(total.price))}</span>
                      </div>
                      {item.securityDeposit && (
                        <div className="flex justify-between text-sm">
                          <span>Security Deposit</span>
                          <span>{formatCurrency(parseFloat(String(item.securityDeposit)))}</span>
                        </div>
                      )}
                      {deliveryOption === 'shipping' && shippingQuote?.available && (
                        <div className="flex justify-between text-sm">
                          <span>Shipping (estimated)</span>
                          <span>{formatCurrency(shippingQuote.amount)}</span>
                        </div>
                      )}
                      <div className="flex justify-between font-bold text-lg pt-2 border-t border-[var(--border)]">
                        <span>Total</span>
                        <span>{formatCurrency(calculateTotalAmount(
                          total.price,
                          calculateServiceFee(total.price),
                          item.securityDeposit ? parseFloat(String(item.securityDeposit)) : 0,
                          deliveryOption === 'shipping' && shippingQuote?.available ? parseFloat(shippingQuote.amount || '0') : 0
                        ))}</span>
                      </div>
                    </div>
                  )}

                  {cartError && (
                    <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm mt-4">
                      {cartError}
                    </div>
                  )}

                  <button
                    onClick={handleAddToCart}
                    disabled={!canAddToCart || addingToCart}
                    className="w-full bg-[var(--forest-green)] hover:bg-[var(--emerald)] text-white font-semibold py-3 rounded-lg mt-4 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {addingToCart ? 'Adding...' : 'Add to Cart'}
                  </button>
                </div>
              )}

              {/* Buy Now Button - for sale items that don't belong to the viewer */}
              {!isOwnItem && isSaleable && item.salePrice && !isRentable && (
                <button className="w-full bg-[var(--forest-green)] hover:bg-[var(--emerald)] text-white font-semibold py-3 rounded-lg mb-6 transition-colors">
                  Buy Now - ${item.salePrice}
                </button>
              )}

              {/* Owner Info */}
              <div className="bg-white rounded-xl p-6 border border-[var(--border)]">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 bg-[var(--forest-green)] text-white rounded-full flex items-center justify-center text-xl font-bold">
                    {item.owner.firstName[0]}
                  </div>
                  <div>
                    <h4 className="font-semibold flex items-center gap-2 text-[var(--text-primary)]">
                      {item.owner.firstName} {item.owner.lastName[0]}.
                      {item.owner.verificationStatus === 'VERIFIED' && <span className="text-green-500 text-sm">✓</span>}
                    </h4>
                    <p className="text-sm text-[var(--text-muted)]">
                      {item.totalRentals} completed rentals
                    </p>
                  </div>
                </div>
              </div>

              {user && !isOwnItem && (
                <div className="mt-3 text-center">
                  <button type="button" onClick={() => setReporting(true)} className="min-h-[44px] px-3 text-sm text-[var(--text-secondary)] underline hover:text-[var(--text-primary)]">Report this listing</button>
                  <ReportDialog isOpen={reporting} targetType="LISTING" targetId={item.id} targetLabel="this listing" onClose={() => setReporting(false)} />
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  )
}
